import React, { useMemo, useState } from "react";
import { Pressable, Text, View } from "react-native";

import AIExhibitNarrationPressable from "../audio/AIExhibitNarrationPressable";
import { useAIExhibitFixedNarration } from "../audio/useAIExhibitFixedNarration";
import { AI_EXHIBIT_COLORS as C } from "../aiExhibitTheme";

const NARRATION_BY_TASK_ID = Object.freeze({
  math: "assistant_task_math",
  current: "assistant_task_current",
  document: "assistant_task_document",
  image: "assistant_task_image",
  code: "assistant_task_code",
});

const NARRATION_BY_PIECE_ID = Object.freeze({
  pretraining: "assistant_piece_pretraining",
  posttraining: "assistant_piece_posttraining",
  tools: "assistant_piece_tools",
});
import { getAIExhibitBatch3Copy } from "../content/aiExhibitBatch3Content";

export default function LLMToAssistantScreen({
  navigate,
  language = "en",
  audioEnabled = false,
}) {
  const { playNarration } = useAIExhibitFixedNarration({
    audioEnabled,
    language,
  });
  const t = getAIExhibitBatch3Copy(language).assistant;
  const [taskId, setTaskId] = useState("math");

  const task = useMemo(
    () => t.tasks.find((item) => item.id === taskId) || t.tasks[0],
    [taskId, t.tasks]
  );

  return (
    <View style={{ padding: 28 }}>
      <Text style={{ color: C.accent, fontSize: 13, fontWeight: "900", letterSpacing: 1.2 }}>{t.eyebrow}</Text>
      <Text style={{ color: C.text, fontSize: 44, lineHeight: 50, fontWeight: "900", marginTop: 8 }}>{t.title}</Text>
      <Text style={{ color: C.muted, fontSize: 18, lineHeight: 27, marginTop: 8, maxWidth: 980 }}>{t.subtitle}</Text>

      <View style={{ flexDirection: "row", alignItems: "center", flexWrap: "wrap", gap: 8, marginTop: 24 }}>
        {t.pipeline.map((item, index) => (
          <React.Fragment key={item}>
            <View style={{ flex: 1, minWidth: 155, padding: 15, borderRadius: 14, borderWidth: 1, borderColor: C.border, backgroundColor: index === t.pipeline.length - 1 ? C.accentSoft : C.surfaceRaised }}>
              <Text style={{ color: C.text, fontSize: 15, fontWeight: "900", textAlign: "center" }}>{item}</Text>
            </View>
            {index < t.pipeline.length - 1 ? (
              <Text style={{ color: C.accent, fontSize: 23, fontWeight: "900" }}>→</Text>
            ) : null}
          </React.Fragment>
        ))}
      </View>

      <Text style={{ color: C.text, fontSize: 20, fontWeight: "900", marginTop: 26 }}>{t.taskPrompt}</Text>

      <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 10, marginTop: 12 }}>
        {t.tasks.map((item) => {
          const active = item.id === taskId;
          return (
            <Pressable
              key={item.id}
              onPress={() => {
                setTaskId(item.id);
                playNarration(NARRATION_BY_TASK_ID[item.id]);
              }}
              style={({ pressed }) => ({
                flex: 1,
                minWidth: 190,
                minHeight: 100,
                padding: 15,
                borderRadius: 15,
                borderWidth: active ? 2 : 1,
                borderColor: active ? C.accent : C.border,
                backgroundColor: active || pressed ? C.accentSoft : C.surfaceRaised,
              })}
            >
              <Text style={{ fontSize: 25 }}>{item.icon}</Text>
              <Text style={{ color: C.text, fontSize: 15, lineHeight: 21, fontWeight: "900", marginTop: 7 }}>{item.title}</Text>
            </Pressable>
          );
        })}
      </View>

      <View style={{ marginTop: 18, padding: 22, borderRadius: 19, borderWidth: 1, borderColor: C.accent, backgroundColor: C.surfaceRaised }}>
        <Text style={{ color: C.text, fontSize: 26, fontWeight: "900" }}>{task.icon} {task.title}</Text>

        <View style={{ flexDirection: "row", flexWrap: "wrap", alignItems: "stretch", gap: 10, marginTop: 18 }}>
          <View style={{ flex: 1, minWidth: 220, padding: 16, borderRadius: 14, backgroundColor: C.surface }}>
            <Text style={{ color: C.accent, fontSize: 12, fontWeight: "900" }}>MODEL</Text>
            <Text style={{ color: C.text, fontSize: 15, lineHeight: 23, marginTop: 6 }}>{task.modelRole}</Text>
          </View>

          <Text style={{ color: C.accent, fontSize: 26, fontWeight: "900", alignSelf: "center" }}>+</Text>

          <View style={{ flex: 1, minWidth: 220, padding: 16, borderRadius: 14, backgroundColor: C.accentSoft }}>
            <Text style={{ color: C.accent, fontSize: 12, fontWeight: "900" }}>{language === "es" ? "CAPACIDAD EXTRA" : "EXTRA CAPABILITY"}</Text>
            <Text style={{ color: C.text, fontSize: 18, lineHeight: 24, fontWeight: "900", marginTop: 6 }}>{task.extra}</Text>
          </View>

          <Text style={{ color: C.accent, fontSize: 26, fontWeight: "900", alignSelf: "center" }}>→</Text>

          <View style={{ flex: 1, minWidth: 220, padding: 16, borderRadius: 14, backgroundColor: C.surface }}>
            <Text style={{ color: C.success, fontSize: 12, fontWeight: "900" }}>{language === "es" ? "RESULTADO" : "RESULT"}</Text>
            <Text style={{ color: C.text, fontSize: 15, lineHeight: 23, marginTop: 6 }}>{task.result}</Text>
          </View>
        </View>

        <Text style={{ color: C.muted, fontSize: 15, lineHeight: 23, marginTop: 16 }}>{task.note}</Text>
      </View>

      <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 12, marginTop: 18 }}>
        {[
          ["pretraining", t.pieces.pretraining],
          ["posttraining", t.pieces.posttraining],
          ["tools", t.pieces.tools],
        ].map(([pieceId, piece]) => (
          <AIExhibitNarrationPressable
            key={pieceId}
            audioEnabled={audioEnabled}
            onPress={() => playNarration(NARRATION_BY_PIECE_ID[pieceId])}
            style={{ flex: 1, minWidth: 280, padding: 17, borderRadius: 15, borderWidth: 1, borderColor: C.border, backgroundColor: C.surfaceRaised }}
          >
            <Text style={{ color: C.text, fontSize: 18, fontWeight: "900" }}>{piece.title}</Text>
            <Text style={{ color: C.muted, fontSize: 14, lineHeight: 22, marginTop: 7 }}>{piece.text}</Text>
          </AIExhibitNarrationPressable>
        ))}
      </View>

      <AIExhibitNarrationPressable
        audioEnabled={audioEnabled}
        onPress={() => playNarration("assistant_key_takeaway")}
        style={{ marginTop: 16, padding: 18, borderRadius: 16, borderLeftWidth: 5, borderWidth: 1, borderColor: C.border, borderLeftColor: C.accent, backgroundColor: C.accentSoft }}
      >
        <Text style={{ color: C.text, fontSize: 18, lineHeight: 27, fontWeight: "800" }}>{t.key}</Text>
      </AIExhibitNarrationPressable>

      <Pressable
        onPress={() => navigate("ai_mistakes")}
        style={({ pressed }) => ({
          marginTop: 20,
          alignSelf: "flex-start",
          minHeight: 58,
          paddingHorizontal: 22,
          paddingVertical: 14,
          borderRadius: 16,
          borderWidth: 1,
          borderColor: C.accent,
          backgroundColor: pressed ? C.surfaceRaised : C.accentSoft,
        })}
      >
        <Text style={{ color: C.text, fontSize: 17, fontWeight: "900" }}>{t.next} →</Text>
      </Pressable>
    </View>
  );
}
